import PropTypes from "prop-types"
import React from "react"
import "stylesheets/blocs/molecules/text/InventoryErrorMessageMolecule.scss"
import { InlineError, Stack } from "@shopify/polaris"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

export const ALL_ENTRIES_DISCARDED_ERROR_TYPE = "type_all_entries_discarded"

// Maps an inventory_type to the domain group used for the all-entries-discarded message.
const ALL_DISCARDED_DOMAIN_GROUP = {
  workplace_equipment_inventories: "assets",
  network_equipment_inventories: "assets",
  business_equipment_inventories: "assets",
  data_center_internal_inventories: "assets",
  data_center_external_inventories: "assets",
  websites: "websites",
  purchased_it_services: "services",
  external_apps: "apps",
}

const InventoryErrorMessageMolecule = ({ fileStatus, inventoryType }) => {
  const { t } = useTranslation()

  /**
   *
   * @param {String} errorSourceType The value of the entry "error_source_type" in the file status object
   * @returns {JSX.Element | null} A <Link> component or null if the error source is the client
   */
  const handleInventoryErrorSourceType = (errorSourceType) => {
    switch (errorSourceType) {
      case "type_aws":
      case "type_unknown_source":
      case "type_verdikt":
        return (
          <Link to={`/contact`}>
            {t(`collect:inventory.statuses_errors.contact`)}
          </Link>
        )

      default:
        return undefined
    }
  }

  /**
   * @description Maps the errors array of the file status to display them as InlineErrors
   * @param {Array} errors The array of errors sent in the file status object
   */
  const handleInventoryErrorMessages = (errors) => {
    return (errors || []).map((error, i) => {
      return <InlineError key={i} message={error} />
    })
  }

  /**
   * @description Adds all JSX.Elements for error type, error source type and errors array of the file status object in one array
   * @returns {Array of JSX.Elements} An array of error messages contained in different types JSX.Elements
   */
  // When every line of the upload was rejected, show a domain-specific message
  // (asset / website / service / generic) instead of the raw error_type label.
  const allEntriesDiscardedMessage = () => {
    const group = ALL_DISCARDED_DOMAIN_GROUP[inventoryType] || "generic"
    return t(`collect:inventory.statuses_errors.all_entries_discarded.${group}`)
  }

  const errorTypeMessage = () => {
    if (fileStatus?.error_type === ALL_ENTRIES_DISCARDED_ERROR_TYPE) {
      return allEntriesDiscardedMessage()
    }
    return t(
      `collect:inventory.statuses_errors.error_type.${fileStatus?.error_type}`
    )
  }

  const displayErrors = () => {
    let allErrors = []
    if (fileStatus) {
      allErrors.push(
        <div key={fileStatus.error_type}>
          <p
            key={fileStatus.error_type}
            className="inventory-error-message__error-type"
          >
            {errorTypeMessage()}
          </p>

          <br />

          <p
            key={fileStatus.error_source_type}
            className="inventory-error-message__error-source-type"
          >
            {handleInventoryErrorSourceType(fileStatus.error_source_type)}
          </p>
        </div>
      )
      allErrors.push(handleInventoryErrorMessages(fileStatus.errors))
    }
    return allErrors
  }

  const fileStatusHasErrors =
    fileStatus?.error_source_type ||
    fileStatus?.error_type ||
    fileStatus?.errors

  if (!fileStatusHasErrors) {
    return null
  }
  return (
    <Stack distribution="center">
      <div className="inventory-error-message">{displayErrors()}</div>
    </Stack>
  )
}

InventoryErrorMessageMolecule.propTypes = {
  fileStatus: PropTypes.object,
  inventoryType: PropTypes.string,
}

export default InventoryErrorMessageMolecule
